import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ImageGallery from '@/components/ImageGallery';
import { getPropertyAddress } from '@/utils/properties';
import { Button } from '@/components/ui/button';
import clsx from 'clsx';
import MapPin from '@/assets/icons/MapPin';
import HashIcon from '@/assets/icons/HashIcon';
import FileLayerIcon from '@/assets/icons/FileLayerIcon';
import PrivateSharingIcon from '@/assets/icons/PrivateSharingIcon';

const PropertiesOverview = ({ allProperties, propertyDocumentStatistics }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getPropertyClassName = () => {
    if (allProperties.length === 1) return 'flex w-full';
    if (allProperties.length === 2) return 'grid md:grid-cols-2';
    if (allProperties.length === 3) return 'grid md:grid-cols-3';
    if (allProperties.length === 5) return 'grid md:grid-cols-[2fr_1fr_1fr]';
    if (allProperties.length > 5) return 'grid md:grid-cols-[1fr_1fr_1fr_1fr]';
    return `grid items-start md:grid-cols-4`;
  };
  const getImageClassName = () => {
    if (allProperties.length === 1) return 'w-full !h-[185px] md:!h-[235px]';
    if (allProperties.length === 2)
      return 'w-[40%] md:w-full !h-[117px] md:!h-[220px]';
    if (allProperties.length === 3)
      return 'w-[40%] md:w-full !h-[117px] md:!h-[147px]';
    if (allProperties.length > 3)
      return 'w-[40%] md:w-full !h-[117px] md:!h-[111px]';
    return `w-1/2 md:!h-[235px]`;
  };

  return (
    <div className={clsx(getPropertyClassName(), 'grid-cols-1 gap-[18px]')}>
      {allProperties.map((property, index) => (
        <div
          key={property.boligmappaNumber}
          className={clsx(
            'w-full flex md:flex-row items-center rounded-lg overflow-hidden gap-3',
            allProperties.length > 1 ? 'md:flex-col' : 'md:border md:bg-white',
            allProperties.length === 1 && 'flex-col',
            index === 0 && allProperties.length === 5 && 'row-span-2 pr-3',
            allProperties.length > 4 &&
              !isExpanded &&
              index > 2 &&
              'hidden md:flex'
          )}
        >
          <ImageGallery
            className={clsx(
              'overflow-hidden rounded-lg',
              allProperties.length === 1 && 'md:rounded-none',
              getImageClassName(),
              index === 0 && allProperties.length === 5 && 'md:!h-[220px]'
            )}
            images={property.gallery}
            showPlaceholder
          />
          <div
            className={clsx(
              'flex flex-col gap-1 md:gap-2 items-start w-[60%] md:w-full',
              allProperties.length > 1
                ? 'px-0'
                : 'grid grid-cols-[5fr_2fr] md:flex md:flex-col py-2 md:px-6 w-full'
            )}
          >
            <Link
              to={`/dashboard/${property.boligmappaNumber}/home`}
              className="flex items-start max-w-full"
            >
              <h3
                className={`text-[18px] leading-[20px] md:text-[20px] md:leading-[25px] md:whitespace-nowrap text-ellipsis md:overflow-hidden ${
                  allProperties?.length > 3
                    ? 'md:text-[18px]'
                    : 'md:text-[23px]'
                } ${
                  allProperties?.length > 1
                    ? 'font-regular md:font-normal'
                    : 'font-medium md:font-bold'
                }`}
              >
                {getPropertyAddress(property)}
              </h3>
              <span className="pl-2 text-[18px] leading-[20px] md:text-[20px] md:leading-[25px]">
                ›
              </span>
            </Link>
            <div
              className={`flex flex-col gap-1 md:gap-2 col-start-1 ${
                allProperties?.length === 1 && 'grid grid-cols-2 md:flex'
              }`}
            >
              <div
                className={`${
                  allProperties.length === 1 ? 'flex' : 'hidden'
                } gap-[5px] md:gap-2 items-center`}
              >
                <MapPin />
                <p
                  className={`text-[15px] ${
                    allProperties?.length > 3
                      ? 'md:text-[14.5px]'
                      : 'md:text-[16px]'
                  }`}
                >
                  {property.address.postalCode || ''}&nbsp;
                  {property.address.postalPlace || ''}
                </p>
              </div>
              {allProperties.length !== 1 && (
                <div className="flex gap-2 items-center">
                  <HashIcon />
                  <p
                    className={`text-[15px] ${
                      allProperties?.length > 3
                        ? 'md:text-[14.5px]'
                        : 'md:text-[16px]'
                    }`}
                  >
                    {property.building.unitNumber || ''}
                  </p>
                </div>
              )}
              {allProperties.length === 1 && (
                <div className="flex gap-[5px] md:gap-2 items-center">
                  <HashIcon />
                  <p
                    className={`text-[15px] ${
                      allProperties?.length > 3
                        ? 'md:text-[14.5px]'
                        : 'md:text-[16px]'
                    }`}
                  >
                    {property.building.unitNumber}
                  </p>
                </div>
              )}
              <div className="flex gap-[5px] md:gap-2 items-center">
                <FileLayerIcon />
                <Link
                  className="flex"
                  to={`/dashboard/${property.boligmappaNumber}/documents`}
                >
                  <p
                    className={`flex items-center text-[15px] ${
                      allProperties?.length > 3
                        ? 'md:text-[14.5px]'
                        : 'md:text-[16px]'
                    }`}
                  >
                    {propertyDocumentStatistics?.find(
                      (el) => el.boligmappaNumber === property.boligmappaNumber
                    ).totalDocumentCount || 0}{' '}
                    dokumenter&nbsp;<span className="hidden md:block">›</span>
                  </p>
                </Link>
              </div>
              {property?.owned && (
                <Link
                  className="flex gap-[5px] md:gap-2 items-center"
                  to={`/dashboard/${property.boligmappaNumber}/more/private-share`}
                >
                  <PrivateSharingIcon width={17} />
                  <p
                    className={`text-[15px] ${
                      allProperties?.length > 3
                        ? 'md:text-[14.5px]'
                        : 'md:text-[16px]'
                    }`}
                  >
                    Del tilgang
                  </p>
                </Link>
              )}
            </div>
            {allProperties.length === 1 && (
              <Button
                className="col-start-2 row-start-1 row-end-3 justify-self-end text-[15px] font-normal shadow-none md:border-red-500 px-[12.5px] py-[9.5px] md:px-[25px] md:py-[13px]"
                variant="outline"
                asChild
              >
                <Link to={`/dashboard/${property.boligmappaNumber}/home`}>
                  <p className="hidden md:block">Gå til bolig</p>
                  <p className="block md:hidden">Til boligen</p>
                </Link>
              </Button>
            )}
          </div>
        </div>
      ))}
      {allProperties.length > 4 && (
        <Button
          variant="link"
          className="text-base font-normal hover:no-underline md:hidden text-black opacity-[50%]"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? 'Vis færre boliger' : 'Vis flere boliger ›'}
        </Button>
      )}
    </div>
  );
};

export default PropertiesOverview;
